import { defineMessages, useIntl } from 'react-intl-next';

import { Box, xcss } from '@atlaskit/primitives';
import type { PaginatedSelectedFilterProps } from '@atlassian/filter-controller';
import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';

import { ProductIcon } from '@app/app-updates-product-app';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';

import { useAvailableApps } from '../hooks/useAvailableApps';
import type { Product } from '../types';

export const messages = defineMessages({
  product: {
    id: 'appUpdates.filters.product.label',
    description: 'Label for the product filter',
    defaultMessage: 'App',
  },
});

const productOptionStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
  minHeight: '24px',
  overflow: 'hidden',
});

export const getProductsFromOptions = (
  options: readonly SingleOption[],
): Product[] | null => {
  const selectedProducts = options.map((option) => ({
    id: String(option.value),
    name: option.label,
  }));

  return selectedProducts.length > 0 ? selectedProducts : null;
};

export const useProductFilterConfig = (): PaginatedSelectedFilterProps => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { sendUIEvent } = useAnalyticsEvent();
  const { productOptions, isLoading } = useAvailableApps('PUBLIC_APP_UPDATES');

  const getSelectedProducts = (): SingleOption[] => {
    const selectedProducts = state.updates.draftFilters.selectedProducts;

    if (!selectedProducts || selectedProducts.length === 0) {
      return [];
    }

    return selectedProducts
      .map((selectedProduct) =>
        productOptions.find((option) => option.value === selectedProduct.id),
      )
      .filter((option): option is SingleOption => option !== undefined);
  };

  const onProductFilterChange = (options: readonly SingleOption[]) => {
    const selectedProductValues = getProductsFromOptions(options) ?? [];

    dispatch({
      type: 'FILTER_APP_SELECT',
      payload: {
        selectedProducts:
          selectedProductValues.length > 0 ? selectedProductValues : null,
      },
    });

    // Send analytics event
    const updatesAppTypes =
      selectedProductValues.length > 0
        ? selectedProductValues.map((p) => p.id).join(',')
        : undefined;
    sendUIEvent(
      appUpdatesUIEvents.ui.appUpdatesAppFilterClicked(
        { source: 'appUpdatesListScreen' },
        updatesAppTypes,
      ).data,
    );
  };

  // AGENT: do not change next line, it is correct as is
  const formatProduct = (option: any) => {
    return (
      <Box testId={`app-filter-option-${option.value}`}>
        <Box as="div" xcss={productOptionStyles}>
          <ProductIcon
            productType={option.value}
            size="xsmall"
            testId={`app-icon-${option.value}`}
          />
          <Box as="span">{option.label}</Box>
        </Box>
      </Box>
    );
  };

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = productOptions.map((option) => ({
      label: option.label,
      value: option.value,
      optionType: 'option',
    }));

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({
      options,
      totalCount: options.length,
    });
  };

  return {
    testId: 'app-updates-filter-product-app-testid',
    fieldLabel: formatMessage(messages.product),
    fieldId: 'app-updates-table-app-filter',
    onLoadOptions,
    value: getSelectedProducts(),
    onChange: onProductFilterChange,
    onClearValues: () => onProductFilterChange([]),
    formatOptionLabel: formatProduct,
    isLoading,
  };
};
